/*
 * <project-hero> — drives the cinematic hero on /pages/make-* hubs.
 *
 * Responsibilities:
 *   - Letter-stagger fade-in on the heading after load
 *   - Parallax: image translates Y at ~0.4x scroll speed while in view
 *   - Subtle scale (1 → 1.04) as user scrolls past the hero
 *   - Honors prefers-reduced-motion (skips parallax + stagger animation)
 *
 * Wrapped in IIFE + customElements.get guard for safety.
 *
 * v1.0 — Sprint 1
 */
(function () {
  if (customElements.get('project-hero')) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class ProjectHero extends HTMLElement {
    constructor() {
      super();
      this.heading = null;
      this.media = null;
      this.ticking = false;
      this.bound = {
        scroll: this.onScroll.bind(this),
        resize: this.onResize.bind(this),
      };
      this.heroRect = null;
    }

    connectedCallback() {
      this.heading = this.querySelector('.project-hero__heading');
      this.media = this.querySelector('.project-hero__media');

      this.splitHeading();
      this.revealHeading();

      if (!reduced) {
        this.measure();
        window.addEventListener('scroll', this.bound.scroll, { passive: true });
        window.addEventListener('resize', this.bound.resize, { passive: true });
        this.onScroll(); // initial position
        this.initDust();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.bound.scroll);
      window.removeEventListener('resize', this.bound.resize);
      this.stopDust();
      if (this.dustIO) this.dustIO.disconnect();
    }

    /* Split heading into character spans for stagger reveal.
       Words stay together (no break inside word). */
    splitHeading() {
      if (!this.heading) return;
      if (this.heading.dataset.split === '1') return;

      const text = this.heading.textContent || '';
      this.heading.textContent = '';
      this.heading.classList.add('project-stagger');

      // Walk char by char but preserve spaces as separators between word groups
      const words = text.split(/(\s+)/);
      let charIndex = 0;
      words.forEach((word) => {
        if (/^\s+$/.test(word)) {
          this.heading.appendChild(document.createTextNode(' '));
          return;
        }
        const wordWrap = document.createElement('span');
        wordWrap.className = 'project-hero__word';
        wordWrap.style.display = 'inline-block';
        wordWrap.style.whiteSpace = 'nowrap';
        for (const ch of word) {
          const span = document.createElement('span');
          span.textContent = ch;
          span.style.setProperty('--stagger-index', charIndex++);
          wordWrap.appendChild(span);
        }
        this.heading.appendChild(wordWrap);
      });
      this.heading.dataset.split = '1';
    }

    revealHeading() {
      if (!this.heading) return;
      // Next frame, kick the reveal
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.heading.classList.add('is-revealed');
        });
      });
    }

    measure() {
      this.heroRect = this.getBoundingClientRect();
      this.heroHeight = this.offsetHeight;
      this.heroTopAbs = window.scrollY + this.heroRect.top;
    }

    onResize() {
      this.measure();
      if (this.dust) {
        this.dustResize();
        this.seedDust();
      }
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.applyScroll();
        this.ticking = false;
      });
    }

    applyScroll() {
      if (!this.heroHeight) return;
      const scrollY = window.scrollY;
      const progress = (scrollY - this.heroTopAbs) / this.heroHeight; // -1..0 entering, 0..1 leaving
      const clamped = Math.max(-0.5, Math.min(1.2, progress));

      // Parallax: image translates downward at 0.32× scroll progress
      const parallaxPx = clamped * this.heroHeight * 0.18;
      if (this.media) {
        this.media.style.setProperty('--parallax-y', `${parallaxPx}px`);
      }

      // Mild zoom as we scroll past
      const zoom = 1 + Math.max(0, clamped) * 0.04;
      if (this.media) {
        this.media.style.setProperty('--hero-zoom', zoom.toFixed(4));
      }
    }

    /* ---- Atmospheric dust motes ----------------------------------
       A lightweight canvas of slow-drifting, twinkling particles that
       glow (screen blend) over the dark hero background. Paused while
       the hero is offscreen. Skipped entirely under reduced-motion. */
    initDust() {
      let ctx;
      try {
        const probe = document.createElement('canvas');
        if (!probe.getContext || !(ctx = probe.getContext('2d'))) return;
      } catch (e) {
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.className = 'project-hero__dust';
      canvas.setAttribute('aria-hidden', 'true');
      this.appendChild(canvas);

      this.dust = {
        canvas: canvas,
        ctx: canvas.getContext('2d'),
        particles: [],
        raf: 0,
        w: 0,
        h: 0,
        dpr: Math.min(window.devicePixelRatio || 1, 2),
        color: '244, 238, 228',
      };

      // Soft palette: warm white, dusty pink, beige.
      this.dust.palette = ['255, 252, 248', '244, 198, 208', '236, 222, 197'];
      this.dust.sprites = this.dust.palette.map((col) => this.makeMoteSprite(col));
      this.dustResize();
      this.seedDust();

      if ('IntersectionObserver' in window) {
        this.dustIO = new IntersectionObserver((entries) => {
          if (entries[0] && entries[0].isIntersecting) this.startDust();
          else this.stopDust();
        }, { threshold: 0 });
        this.dustIO.observe(this);
      } else {
        this.startDust();
      }

      requestAnimationFrame(() => canvas.classList.add('is-active'));
    }

    hexToRgb(hex) {
      if (!hex) return null;
      const m = hex.replace('#', '').match(/^([0-9a-f]{3}|[0-9a-f]{6})$/i);
      if (!m) return null;
      let h = m[1];
      if (h.length === 3) h = h.split('').map((c) => c + c).join('');
      const n = parseInt(h, 16);
      return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
    }

    /* Pre-render a soft radial glow sprite once, then stamp it per
       particle with globalAlpha — cheaper and prettier than per-frame
       gradients or shadowBlur. */
    makeMoteSprite(color) {
      const size = 64;
      const c = document.createElement('canvas');
      c.width = size;
      c.height = size;
      const g = c.getContext('2d');
      const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
      grad.addColorStop(0, 'rgba(' + color + ', 1)');
      grad.addColorStop(0.35, 'rgba(' + color + ', 0.55)');
      grad.addColorStop(1, 'rgba(' + color + ', 0)');
      g.fillStyle = grad;
      g.fillRect(0, 0, size, size);
      return c;
    }

    dustResize() {
      const d = this.dust;
      if (!d) return;
      const rect = this.getBoundingClientRect();
      d.w = Math.max(1, Math.floor(rect.width));
      d.h = Math.max(1, Math.floor(rect.height));
      d.canvas.width = Math.floor(d.w * d.dpr);
      d.canvas.height = Math.floor(d.h * d.dpr);
      d.canvas.style.width = d.w + 'px';
      d.canvas.style.height = d.h + 'px';
      d.ctx.setTransform(d.dpr, 0, 0, d.dpr, 0, 0);
    }

    seedDust() {
      const d = this.dust;
      if (!d) return;
      const count = Math.max(40, Math.min(160, Math.round((d.w * d.h) / 9000)));
      d.particles = [];
      for (let i = 0; i < count; i++) {
        d.particles.push({
          x: Math.random() * d.w,
          y: Math.random() * d.h,
          r: 0.6 + Math.random() * 1.8,
          a: 0.18 + Math.random() * 0.42,
          vy: -(0.06 + Math.random() * 0.22),
          vx: (Math.random() - 0.5) * 0.12,
          sway: Math.random() * Math.PI * 2,
          swaySpeed: 0.002 + Math.random() * 0.004,
          twinkle: 0.4 + Math.random() * 0.6,
          tw: Math.random() * Math.PI * 2,
          s: Math.floor(Math.random() * d.sprites.length),
        });
      }
    }

    startDust() {
      const d = this.dust;
      if (!d || d.raf) return;
      const loop = () => {
        this.drawDust();
        d.raf = requestAnimationFrame(loop);
      };
      d.raf = requestAnimationFrame(loop);
    }

    stopDust() {
      const d = this.dust;
      if (!d || !d.raf) return;
      cancelAnimationFrame(d.raf);
      d.raf = 0;
    }

    drawDust() {
      const d = this.dust;
      if (!d) return;
      const ctx = d.ctx;
      const w = d.w;
      const h = d.h;
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < d.particles.length; i++) {
        const p = d.particles[i];
        p.sway += p.swaySpeed;
        p.tw += 0.02;
        p.x += p.vx + Math.sin(p.sway) * 0.18;
        p.y += p.vy;
        if (p.y < -8) { p.y = h + 8; p.x = Math.random() * w; }
        if (p.x < -8) p.x = w + 8;
        else if (p.x > w + 8) p.x = -8;
        const alpha = Math.max(0, Math.min(1, p.a * (0.6 + 0.4 * Math.sin(p.tw) * p.twinkle)));
        const d2 = p.r * 5;
        ctx.globalAlpha = alpha;
        ctx.drawImage(d.sprites[p.s], p.x - d2 / 2, p.y - d2 / 2, d2, d2);
      }
      ctx.globalAlpha = 1;
    }
  }

  customElements.define('project-hero', ProjectHero);
})();
